import { useRef, useState, useCallback, useEffect } from 'react';
import { useAudioMixer } from './useAudioMixer';
import {
  saveChunk,
  getAllChunks,
  clearChunks,
} from "../utils/recordingStorage";
/**
 * useScreenRecorder
 *
 * Hook principal consommé par MediaControlPanel.
 * Gère le cycle de vie complet de l'enregistrement :
 *  - Capture audio mixée (micro + son système) via useAudioMixer
 *  - Enregistrement via MediaRecorder
 *  - Prévisualisation et téléchargement du fichier final
 *
 * Retourne :
 *  - status            → 'idle' | 'recording' | 'preview'
 *  - isRecording       → boolean, true si status === 'recording'
 *  - duration          → durée formatée "00:00:00" pendant l'enregistrement
 *  - videoUrl          → URL blob du fichier après stop (pour PreviewModal)
 *  - startRecording()  → démarre immédiatement l'enregistrement
 *  - stopRecording()   → arrête et génère le fichier
 *  - downloadRecording()→ déclenche le téléchargement du fichier
 *  - resetRecording()  → remet tout à zéro (ferme PreviewModal)
 */
export function useScreenRecorder(micActive) {
  const [status, setStatus]     = useState('idle');
  const [videoUrl, setVideoUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const blobRef          = useRef(null);

  const micGainNodeRef = useRef(null);
  

  const { initAudio, cleanup } = useAudioMixer();

  // -------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------

  const getBestMimeType = () => {
    const candidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ];
    return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
  };

  // -------------------------------------------------------------
  // startRecording
  // Démarre immédiatement la capture audio/vidéo.
  // -------------------------------------------------------------
  
  const startRecording = useCallback(async () => {
    try {
      // 1. Initialise le mixage audio (micro + son système)
      const { mixedStream, micGainNode } = await initAudio();

      micGainNodeRef.current = micGainNode;
      // état initial du micro
      micGainNodeRef.current.gain.value = micActive ? 1 : 0;
      // 2. Crée le MediaRecorder avec le stream mixé
      const mimeType = getBestMimeType();
      const recorder  = new MediaRecorder(mixedStream, {
        mimeType: mimeType || undefined,
        audioBitsPerSecond: 128000,
        videoBitsPerSecond: 2500000,
      });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      // Accumule les données toutes les secondes , surmonte les crash
      recorder.ondataavailable = async (e) => {

        if (e.data && e.data.size > 0) {
      
          chunksRef.current.push(e.data);
      
          try {
      
            await saveChunk(e.data);
      
          } catch (err) {
      
            console.error(
              "[IndexedDB] saveChunk error:",
              err
            );
          }
        }
      };

      // Quand le recorder s'arrête → génère le blob et passe en preview
      recorder.onstop = async () => {

        const mimeUsed = mimeType || 'video/webm';
      
        const savedChunks = await getAllChunks();
      
        const blob = new Blob(savedChunks, {
          type: mimeUsed,
        });
      
        blobRef.current = blob;
      
        const url = URL.createObjectURL(blob);
      
        setVideoUrl(url);
        setStatus('preview');      
        cleanup();
      };

      // Si l'utilisateur ferme le partage d'écran via le navigateur
      mixedStream.getVideoTracks()[0]?.addEventListener('ended', () => {
        if (mediaRecorderRef.current?.state !== 'inactive') {
          stopRecording();
        }
      });

      recorder.start(1000);
      setStatus('recording');

      return true;

    } catch (err) {
      // Remet à zéro si permission refusée ou autre erreur
      cleanup();
      setStatus('idle');
      return false;
    }
  }, [initAudio, cleanup, micActive]);

  // -------------------------------------------------------------
  // stopRecording
  // Arrête le MediaRecorder → déclenche onstop → passe en preview
  // -------------------------------------------------------------
  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // -------------------------------------------------------------
  // downloadRecording
  // Crée un lien temporaire et déclenche le téléchargement
  // -------------------------------------------------------------
  const downloadRecording = useCallback(async () => {
    if (!videoUrl || !blobRef.current) return;

    const ext  = blobRef.current.type.includes('mp4') ? 'mp4' : 'webm';
    const date = new Date().toISOString().slice(0, 10);
    const name = `reunion-${date}.${ext}`;

    const a  = document.createElement('a');
    a.href   = videoUrl;
    a.download = name;
    a.click();
    await clearChunks();
  }, [videoUrl]);

  // -------------------------------------------------------------
  // resetRecording
  // Ferme la PreviewModal et remet tout à zéro
  // -------------------------------------------------------------
  const resetRecording = useCallback(async () => {
    
    await clearChunks();
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl); // libère la mémoire
    }
    setVideoUrl(null);
    setStatus('idle');
    chunksRef.current  = [];
    blobRef.current    = null;
  }, [videoUrl]);

  useEffect(() => {
    if (!micGainNodeRef.current) return;
  
    micGainNodeRef.current.gain.value = micActive ? 1 : 0;
  }, [micActive]);

  useEffect(() => {

    const recoverRecording = async () => {
  
      try {
  
        const savedChunks = await getAllChunks();
  
        if (!savedChunks.length) return;
  
        
        const recoveredBlob = new Blob(savedChunks, {
          type: "video/webm",
        });
  
        blobRef.current = recoveredBlob;
  
        const url = URL.createObjectURL(
          recoveredBlob
        );
  
        setVideoUrl(url);
  
        setStatus("preview");
  
      } catch (err) {
  
        console.error(
          "[IndexedDB] recover error:",
          err
        );
      }
    };
  
    recoverRecording();
  
  }, []);

  return {
    status,
    isRecording: status === 'recording',
    videoUrl,
    startRecording,
    stopRecording,
    downloadRecording,
    resetRecording,
  };
}